import { useEffect, useRef } from 'react';
import { useGoogleMap } from '@react-google-maps/api';
import { HeatmapContact } from './useHeatmapData';
import { createMarkerIcon } from './markerIconUtils';
import { createInfoWindowContent } from './infoWindowContent';

interface ContactMarkersComponentProps {
  contacts: HeatmapContact[];
}

export const ContactMarkersComponent = ({ contacts }: ContactMarkersComponentProps) => {
  const map = useGoogleMap();
  const markersRef = useRef<google.maps.Marker[]>([]);
  const infoWindowsRef = useRef<google.maps.InfoWindow[]>([]);

  useEffect(() => {
    if (!map || !window.google || !contacts || contacts.length === 0) {
      return;
    }

    // Clear existing markers and info windows
    markersRef.current.forEach(marker => {
      marker.setMap(null);
    });
    markersRef.current = [];

    infoWindowsRef.current.forEach(infoWindow => {
      infoWindow.close();
    });
    infoWindowsRef.current = [];

    // Filter contacts with valid locations
    const validContacts = contacts.filter(contact => contact.address?.lat && contact.address?.lon);

    // Create markers for each contact
    validContacts.forEach(contact => {
      if (!contact.address?.lat || !contact.address?.lon) return;

      const position = new google.maps.LatLng(contact.address.lat, contact.address.lon);

      // Use activitiesCount from backend (already calculated and filtered)
      // Fallback to activities.length if activitiesCount is not available
      const activityCount = contact.activitiesCount ?? contact.activities?.length ?? 0;

      // Create custom icon with activity count
      const icon = createMarkerIcon(activityCount);

      // Create marker
      const marker = new google.maps.Marker({
        position,
        map,
        icon,
        optimized: false,
        clickable: true
      });

      // Create InfoWindow with our content
      const infoWindowContent = createInfoWindowContent(contact);
      const infoWindow = new google.maps.InfoWindow({
        content: infoWindowContent
      });

      // Open InfoWindow when marker is clicked
      marker.addListener('click', () => {
        // Close all other info windows first
        infoWindowsRef.current.forEach(iw => {
          if (iw !== infoWindow) {
            iw.close();
          }
        });
        infoWindow.open(map, marker);
      });

      markersRef.current.push(marker);
      infoWindowsRef.current.push(infoWindow);
    });

    // Cleanup function
    return () => {
      markersRef.current.forEach(marker => {
        marker.setMap(null);
        google.maps.event.clearInstanceListeners(marker);
      });
      markersRef.current = [];

      infoWindowsRef.current.forEach(infoWindow => {
        infoWindow.close();
      });
      infoWindowsRef.current = [];
    };
  }, [map, contacts]);

  return null;
};
